import React, { useState, useEffect, useCallback } from 'react';
import { Material, ModifierApplication, Process } from '../types';
import { MaterialService } from '../services/materialService';
import { ProcessService } from '../services/processService';
import { calculateMaterialLineItem, formatCurrency } from '../utils/calculations';
import '../styles/MaterialSelectorModal.css';

interface MaterialSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (material: Material, quantity: number, modifiers: ModifierApplication[]) => void;
  momentName?: string;
}

interface ProcessConfig {
  processId: string;
  costType: 'flat_rate' | 'per_sqft';
  sqftAmount: number;
}

export const MaterialSelectorModal: React.FC<MaterialSelectorModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  momentName = 'Item',
}) => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [processes, setProcesses] = useState<Process[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [processConfigs, setProcessConfigs] = useState<Map<string, ProcessConfig>>(new Map());
  const [previewCalculation, setPreviewCalculation] = useState<any>(null);

  const buildModifiersFromConfigs = useCallback((): ModifierApplication[] => {
    if (processes.length === 0) return [];

    const modifiers: ModifierApplication[] = [];

    Array.from(processConfigs.values()).forEach((config) => {
      const process = processes.find(p => p.id === config.processId);
      if (!process) return;

      let costValue = 0;
      if (config.costType === 'flat_rate' && process.flatRateCost) {
        costValue = process.flatRateCost;
      } else if (config.costType === 'per_sqft' && process.sqftCost) {
        costValue = process.sqftCost * config.sqftAmount;
      }

      modifiers.push({
        processId: config.processId,
        processName: process.name,
        costType: config.costType,
        costValue,
        sqftAmount: config.costType === 'per_sqft' ? config.sqftAmount : undefined,
        isSelected: true,
      });
    });

    return modifiers;
  }, [processes, processConfigs]);

  useEffect(() => {
    if (isOpen) {
      loadMaterials();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedMaterial && quantity > 0) {
      const modifiers = buildModifiersFromConfigs();
      const calculation = calculateMaterialLineItem(selectedMaterial, quantity, modifiers);
      setPreviewCalculation(calculation);
    }
  }, [selectedMaterial, quantity, buildModifiersFromConfigs]);

  const loadMaterials = async () => {
    try {
      setLoading(true);
      setError(null);

      const [materialsData, categoriesData, processesData] = await Promise.all([
        MaterialService.getAllMaterials(),
        MaterialService.getCategories(),
        ProcessService.getProcesses(),
      ]);

      setMaterials(materialsData);
      setCategories([{ id: 'all', name: 'All Materials' }, ...categoriesData]);
      setProcesses(processesData.filter(p => p.isActive));
      setSelectedCategory('all');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredMaterials = materials.filter((material) => {
    const matchesCategory =
      selectedCategory === 'all' || material.categoryId === selectedCategory;
    const matchesSearch =
      material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      material.color.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleSelectMaterial = (material: Material) => {
    setSelectedMaterial(material);
    setProcessConfigs(new Map());
    setQuantity(1);
  };

  const handleToggleProcess = (processId: string, initialCostType: 'flat_rate' | 'per_sqft') => {
    const updated = new Map(processConfigs);
    if (updated.has(processId)) {
      updated.delete(processId);
    } else {
      updated.set(processId, {
        processId,
        costType: initialCostType,
        sqftAmount: 1,
      });
    }
    setProcessConfigs(updated);
  };

  const handleUpdateProcessConfig = (processId: string, updates: Partial<ProcessConfig>) => {
    const updated = new Map(processConfigs);
    const existing = updated.get(processId);
    if (existing) {
      updated.set(processId, { ...existing, ...updates });
      setProcessConfigs(updated);
    }
  };

  const handleAddToQuote = () => {
    if (!selectedMaterial) return;

    const modifiers = buildModifiersFromConfigs();
    onSelect(selectedMaterial, quantity, modifiers);
    resetForm();
  };

  const resetForm = () => {
    setSelectedMaterial(null);
    setQuantity(1);
    setProcessConfigs(new Map());
    setPreviewCalculation(null);
    setSearchTerm('');
  };

  if (!isOpen) return null;

  return (
    <div className="material-modal-overlay" onClick={onClose}>
      <div className="material-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="material-modal-header">
          <div>
            <h3>Add Material to {momentName}</h3>
            <p>Select a material and configure quantity</p>
          </div>
          <button onClick={onClose} className="material-modal-close">
            ×
          </button>
        </div>

        {error && (
          <div className="material-modal-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="material-modal-body">
          {loading ? (
            <div className="material-modal-loading">
              <div className="spinner"></div>
              <p>Loading materials...</p>
            </div>
          ) : (
            <div className="material-modal-layout">
              {/* Left side: Material list */}
              <div className="material-list-section">
                <div className="material-controls">
                  <input
                    type="text"
                    placeholder="Search materials..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="material-search"
                  />

                  <div className="category-filter">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        className={`category-button ${selectedCategory === cat.id ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(cat.id)}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="materials-list">
                  {filteredMaterials.length === 0 ? (
                    <div className="empty-list">
                      <p>No materials found</p>
                    </div>
                  ) : (
                    filteredMaterials.map((material) => (
                      <div
                        key={material.id}
                        className={`material-list-item ${selectedMaterial?.id === material.id ? 'selected' : ''}`}
                        onClick={() => handleSelectMaterial(material)}
                      >
                        <div className="material-list-item-header">
                          <h4>{material.name}</h4>
                          <span className="material-price">
                            {formatCurrency(material.basePrice)}
                          </span>
                        </div>
                        <p className="material-list-item-details">
                          {material.thickness} • {material.size} • {material.color}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Right side: Configuration */}
              <div className="material-config-section">
                {selectedMaterial ? (
                  <>
                    <div className="material-detail">
                      <h4>Selected Material</h4>
                      <div className="material-detail-info">
                        <div className="detail-row">
                          <span>Name:</span>
                          <strong>{selectedMaterial.name}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Thickness:</span>
                          <span>{selectedMaterial.thickness}</span>
                        </div>
                        <div className="detail-row">
                          <span>Size:</span>
                          <span>{selectedMaterial.size}</span>
                        </div>
                        <div className="detail-row">
                          <span>Color:</span>
                          <span>{selectedMaterial.color}</span>
                        </div>
                        <div className="detail-row">
                          <span>Base Price:</span>
                          <span>{formatCurrency(selectedMaterial.basePrice)}</span>
                        </div>
                        <div className="detail-row">
                          <span>Markup:</span>
                          <span>{((selectedMaterial.markupFactor - 1) * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                    </div>

                    <div className="quantity-selector">
                      <label htmlFor="quantity">Quantity</label>
                      <div className="quantity-input-group">
                        <button
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="qty-button"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          id="quantity"
                          min="1"
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          className="qty-input"
                        />
                        <button
                          onClick={() => setQuantity(quantity + 1)}
                          className="qty-button"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {processes && processes.length > 0 && (
                      <div className="modifiers-section">
                        <h4>Available Processes</h4>
                        <p className="section-subtitle">Select processes to apply to this material</p>
                        <div className="processes-list">
                          {processes.map((process) => {
                            const config = processConfigs.get(process.id);
                            const isSelected = !!config;
                            const defaultCostType = process.flatRateCost ? 'flat_rate' : 'per_sqft';

                            return (
                              <div key={process.id} className="process-item">
                                <div className="process-header">
                                  <label className="process-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleToggleProcess(process.id, defaultCostType)}
                                    />
                                    <span className="process-name">{process.name}</span>
                                  </label>
                                </div>

                                {isSelected && (
                                  <div className="process-options">
                                    {process.flatRateCost && (
                                      <label className="cost-option">
                                        <input
                                          type="radio"
                                          name={`cost-type-${process.id}`}
                                          value="flat_rate"
                                          checked={config?.costType === 'flat_rate'}
                                          onChange={() => handleUpdateProcessConfig(process.id, { costType: 'flat_rate' })}
                                        />
                                        <span>Flat Rate: {formatCurrency(process.flatRateCost)}</span>
                                      </label>
                                    )}

                                    {process.sqftCost && (
                                      <label className="cost-option">
                                        <input
                                          type="radio"
                                          name={`cost-type-${process.id}`}
                                          value="per_sqft"
                                          checked={config?.costType === 'per_sqft'}
                                          onChange={() => handleUpdateProcessConfig(process.id, { costType: 'per_sqft' })}
                                        />
                                        <span>Per Sqft: {formatCurrency(process.sqftCost)}/sqft</span>
                                      </label>
                                    )}

                                    {config?.costType === 'per_sqft' && (
                                      <div className="sqft-input-group">
                                        <label htmlFor={`sqft-${process.id}`}>Square Footage:</label>
                                        <input
                                          type="number"
                                          id={`sqft-${process.id}`}
                                          min="0.01"
                                          step="0.01"
                                          value={config.sqftAmount}
                                          onChange={(e) => handleUpdateProcessConfig(process.id, {
                                            sqftAmount: parseFloat(e.target.value) || 0
                                          })}
                                          className="sqft-input"
                                        />
                                        <span className="sqft-cost">
                                          = {formatCurrency((process.sqftCost || 0) * config.sqftAmount)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {previewCalculation && (
                      <div className="calculation-preview">
                        <h4>Pricing Preview</h4>
                        <div className="preview-rows">
                          <div className="preview-row">
                            <span>Unit Cost (before markup):</span>
                            <span>{formatCurrency(previewCalculation.unitCostBeforeMarkup)}</span>
                          </div>
                          <div className="preview-row">
                            <span>Unit Cost (after markup):</span>
                            <strong>{formatCurrency(previewCalculation.unitCostAfterMarkup)}</strong>
                          </div>
                          <div className="preview-row">
                            <span>Line Total (qty × unit):</span>
                            <strong>{formatCurrency(previewCalculation.totalCostAfterMarkup)}</strong>
                          </div>
                        </div>
                      </div>
                    )}

                    <button onClick={handleAddToQuote} className="add-material-button">
                      Add to Quote
                    </button>
                    <button onClick={resetForm} className="reset-button">
                      Clear Selection
                    </button>
                  </>
                ) : (
                  <div className="material-config-empty">
                    <p>👈 Select a material to configure</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
